import { html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './employment-v2.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';
import '../../../edit-card/edit-card.component.js';
import { validateForm } from '../../../../shared/decorators/validate-form.decorator.js';
import { REFI_PERSONAL_CONST } from '../../../../shared/constants/app.constants.js';
import { IconicV2BaseComponent } from '../../../iconic-v2-base/iconic-v2-base.component.js';
import '../employment-active-toggle-v2/employment-active-toggle-v2.component.js';
import '../step-income-v2/step-iconic-income-v2.component.js';
import { currencyFormatter } from '../../../../shared/formatters/currency.formatter.js';

class RefiPersonalEmploymentComponent extends IconicV2BaseComponent {
  constructor() {
    super();
    this.initializeProperties();
  }

  static properties = {
    applicantType: { type: String, attribute: 'applicant-type' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    displayError: { type: Boolean, attribute: 'display-error', reflect: true },
    compact: { type: Boolean, reflect: true },
    confirmationPage: {
      type: Boolean,
      reflect: true,
      attribute: 'confirmation-page',
    },
    mockApplication: {
      type: Boolean,
      reflect: true,
      attribute: 'mock-application',
    },
    inactive: { type: Boolean, attribute: 'inactive', reflect: true },
    missingEmployment: { type: Boolean, reflect: true },
    displayEmploymentComponent: { type: Boolean },
  };

  static styles = [style, globalStyle];

  initializeProperties() {
    this.validity = {};
    this.displayError = false;
    this.applicantType = 'primaryApplicant';
    this.coAppPrefix = '';
    this.childIds = {};
    this.compact = false;
    this.employmentStatus = '';
    this.employedOrSelfEmployed = true;
    this.inputChildren = {};
    this.values = {};
    this.missingEmployment = false;
    this.cardFired = false;
    this.coCardFired = false;
    this.fieldsToCheck = [];
    this.confirmationPage = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this.initializeApplicantData();
  }

  initializeApplicantData() {
    this.application = ApplicationService.Application;
    this.isPrimary = !(this.applicantType === 'coApplicant');
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.setValues();
    this.cardFormId = `${this.coAppPrefix}employment-cardForm`;
    this.employmentStatus =
      this.application[this.applicantType].employmentStatus;
    this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
      this.employmentStatus
    );
    this.displayEmploymentComponent = this.showEmploymentComponent();
    this.childIds = {
      job: `${this.coAppPrefix}job-input`,
      jobStatus: `${this.coAppPrefix}job-status-input`,
      employmentActiveToggle: `${this.coAppPrefix}employment-active-toggle`,
      income: `${this.coAppPrefix}income-input`,
    };
    this.inputChildren = {
      job: `${this.coAppPrefix}job-input`,
      jobStatus: `${this.coAppPrefix}job-status-input`,
      employmentActiveToggle: `${this.coAppPrefix}employment-active-toggle`,
      income: `${this.coAppPrefix}income-input`,
    };
    this.initialValues = {
      employmentStatus: this.application.primaryApplicant.employmentStatus,
      jobDescription:
        this.application.primaryApplicant.employmentInfo.jobDescription,
    };
    this.initialCoAppValues = {
      employmentStatus: this.application.coApplicant.employmentStatus,
      jobDescription:
        this.application.coApplicant.employmentInfo.jobDescription,
    };
  }

  setValues() {
    this.values = {
      job: this.application[this.applicantType].employmentStatus || '',
      jobDescription:
        this.application[this.applicantType].employmentInfo.jobDescription ||
        '',
    };
  }

  firstUpdated() {
    super.firstUpdated();

    setTimeout(() => {
      this.inputChildren.employmentActiveToggle =
        this.shadowRoot.getElementById(this.childIds.employmentActiveToggle);
      this.inputChildren.income = this.shadowRoot.getElementById(
        this.childIds.income
      );
      this.inputChildren.job = this.shadowRoot.getElementById(
        this.childIds.job
      );
    }, 0);
  }

  showEmploymentComponent() {
    return (
      this.isPrimary &&
      ((this.employedOrSelfEmployed && this.values.jobDescription) ||
        (!this.employedOrSelfEmployed && this.values.job !== ''))
    );
  }

  publishViewTag() {
    const tagKey = this.isPrimary
      ? TagKey.VIEW_EMPLOYMENT_REVIEW
      : TagKey.VIEW_COBORROWER_EMPLOYMENT_REVIEW;
    stratum.publishTag(tagKey, {
      replacements: {
        value: this.isPrimary
          ? 'Employment review component shown'
          : 'Co-borrower employment review component shown',
      },
    });
  }

  renderFields() {
    return html` <div class="employment-fields">
      ${this.renderJobStatusSelect()} ${this.renderJobDescriptionField()}
      ${this.renderPFCIandJobTitle()} ${this.renderIncomeFields()}
    </div>`;
  }

  renderJobStatusSelect() {
    return html` <fs-select
      id=${this.childIds.jobStatus}
      name="job-status-select"
      required
      value-missing-message="Please select a job status"
      @change="${this.employmentChange}"
      value="${this.values.job}"
      @validity="${this.updateValidity}"
    >
      <span slot="label">Job status</span>
      ${this.renderJobStatusOptions()}
    </fs-select>`;
  }

  renderJobStatusOptions() {
    const statuses = [
      { value: '', text: 'Pick a job status' },
      {
        value: REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED,
        text: 'Employed',
      },
      {
        value: REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.UNEMPLOYED,
        text: 'Unemployed',
      },
      {
        value: REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED,
        text: 'Self-Employed/1099 contractor',
      },
      { value: REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.RETIRED, text: 'Retired' },
      { value: REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.OTHER, text: 'Other' },
    ];
    return statuses.map(
      (status) => html` <option
        ?selected="${this.values.job.toLowerCase() === status.value}"
        value=${status.value}
      >
        ${status.text}
      </option>`
    );
  }

  renderJobDescriptionField() {
    return this.employedOrSelfEmployed
      ? html` <refi-personal-job
          id="${this.childIds.job}"
          @validity="${this.updateValidity}"
          ?is-primary="${this.isPrimary}"
          pq-v2
        ></refi-personal-job>`
      : nothing;
  }

  employmentChange(ev) {
    this.values.job = ev.target.value;
    this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
      this.values.job
    );
    this.publishEmploymentChangeTag();
    this.dispatchEmploymentChangeEvent();
    this.requestUpdate();
  }

  isEmployedOrSelfEmployed(status) {
    return (
      status === REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
      status === REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED
    );
  }

  publishEmploymentChangeTag() {
    stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
      replacements: {
        value: `${this.coAppPrefix}employmentStatus`,
      },
    });
  }

  dispatchEmploymentChangeEvent() {
    const eventName = this.isPrimary
      ? 'primary-employment-changed'
      : 'coapp-employment-changed';
    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail: { employedOrSelfEmployed: this.employedOrSelfEmployed },
      })
    );
  }

  publishEditViewTag() {
    const tagKey = this.isPrimary
      ? TagKey.VIEW_EMPLOYMENT_EDIT
      : TagKey.VIEW_COBORROWER_EMPLOYMENT_EDIT;
    stratum.publishTag(tagKey);
  }

  renderCardForm() {
    return html` <refi-personal-edit-card
      card-title="Employment"
      component-id="employment-edit-card"
      id="${this.coAppPrefix}employment-edit-card"
      card-icon="fs-24-suitcase"
      .displayText="${this.getDisplayText()}"
      ?inactive="${this.inactive}"
      ?show-error="${this.displayError}"
      @edit-clicked="${this.editClicked}"
      @save-clicked="${this.saveClicked}"
      @cancel-clicked="${this.cancelClicked}"
    >
      <form
        autocomplete="off"
        novalidate
        @submit="${this.onSubmit}"
        id="${this.cardFormId}"
      >
        ${this.renderFields()}
      </form>
    </refi-personal-edit-card>`;
  }

  getDisplayText() {
    this.employedOrSelfEmployedApplication = this.isEmployedOrSelfEmployed(
      this.application[this.applicantType].employmentStatus
    );

    const displayText = [
      this.formatEmploymentStatus(
        this.application[this.applicantType].employmentStatus.toLowerCase()
      ),
    ];

    if (this.employedOrSelfEmployedApplication) {
      displayText.push(
        this.application[this.applicantType].employmentInfo.jobDescription
      );
    }

    if (this.confirmationPage) {
      let incomeValue = this.application[this.applicantType].incomeAmount;
      incomeValue =
        incomeValue !== '' ? currencyFormatter(Math.round(incomeValue)) : '';
      if (incomeValue !== '') {
        displayText.push(`${incomeValue}/annually`);
      }
    }

    return displayText;
  }

  render() {
    if (this.displayEmploymentComponent || this.confirmationPage) {
      if (!this.cardFired) {
        this.cardFired = true;
        this.publishViewTag();
      }
      return html`${this.renderCardForm()}`;
    }
    if (!this.isPrimary && !this.coCardFired) {
      this.coCardFired = true;
      stratum.publishTag(TagKey.VIEW_COBORROWER_EMPLOYMENT_EDIT);
    }
    return html`${this.renderFields()}`;
  }

  renderPFCIandJobTitle() {
    if (
      (this.applicantType === 'coApplicant' || this.confirmationPage) &&
      this.employedOrSelfEmployed
    ) {
      return html`<div class="employment-toggle">
        <refi-personal-employment-active-toggle-v2
          id="${this.childIds.employmentActiveToggle}"
          ?is-prefill="${this.isPrefill}"
          @validity="${this.updateValidity}"
          ?is-primary="${this.isPrimary}"
        ></refi-personal-employment-active-toggle-v2>

        ${this.renderToggleError()}
      </div>`;
    }
    return html``;
  }

  renderToggleError() {
    return this.missingEmployment
      ? html`
          <div class="missing-toggle">
            <fs-helper-text
              type="error"
              error="Please select an option above"
              id="${this.coAppPrefix}employmentActiveInvalid"
            ></fs-helper-text>
          </div>
        `
      : html``;
  }

  renderIncomeFields() {
    if (this.applicantType === 'coApplicant' || this.confirmationPage) {
      return html` <div class="income">
        <refi-step-iconic-income-v2
          id="${this.childIds.income}"
          ?is-prefill="${this.application.isPrefilled}"
          ?is-primary="${this.isPrimary}"
          @validity="${this.updateValidity}"
        ></refi-step-iconic-income-v2>
      </div>`;
    }
    return html``;
  }

  updateValidity(ev) {
    this.validity[`${ev.detail.name}`] = ev.detail.isValid;
    if (ev.detail.name === 'activeIncomeVerification') {
      this.missingEmployment = false;
    }

    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: `${this.coAppPrefix}${ev.detail.name}`,
          isValid: ev.detail.isValid,
        },
      })
    );
    this.requestUpdate();
  }

  cancelClicked() {
    stratum.publishTag(TagKey.PQ_CONFIRMATION_CANCEL_CLICKED, {
      replacements: {
        value: `${this.isPrimary ? '' : 'co borrower '}employment`,
      },
    });
    this.resetValues();
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('edit-card-closed', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-personal-employment-v2`,
        },
      })
    );
    this.displayError = false;
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.values.job = this.application[this.applicantType].employmentStatus;
    this.values.jobDescription =
      this.application[this.applicantType].employmentInfo.jobDescription;
    this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
      this.values.job
    );
    setTimeout(() => {
      this.shadowRoot.getElementById(this.childIds.jobStatus).value =
        this.values.job;
      if (this.employedOrSelfEmployed) {
        this.shadowRoot.getElementById(this.childIds.job).value =
          this.values.jobDescription;
      }

      this.inputChildren.employmentActiveToggle?.resetValues();
      this.inputChildren.income?.resetValues();

      this.requestUpdate();
    }, 0);
  }

  editClicked() {
    stratum.publishTag(TagKey.PQ_CONFIRMATION_EDIT_CLICKED, {
      replacements: {
        value: `${this.isPrimary ? '' : 'co borrower '}employment`,
      },
    });
    this.publishEditViewTag();
    if (this.employedOrSelfEmployed) {
      this.shadowRoot.getElementById(this.childIds.job).resetValues();
    }
    this.values.job = this.application[this.applicantType].employmentStatus;
    this.values.jobDescription =
      this.application[this.applicantType].employmentInfo.jobDescription;
    this.dispatchEvent(
      new CustomEvent('edit-card-open', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-personal-employment-v2`,
        },
      })
    );
  }

  async checkMissingEmployment() {
    this.debug.log(
      'Active income verification',
      !this.validity.activeIncomeVerification
    );
    this.debug.log('Employed or self employed', this.employedOrSelfEmployed);

    const employmentInvalid =
      !this.validity.activeIncomeVerification && this.employedOrSelfEmployed;

    if (!this.employedOrSelfEmployed) {
      this.updateValidity({
        detail: { name: 'jobDescription', isValid: true },
      });
    }

    if (employmentInvalid && (this.confirmationPage || !this.isPrimary)) {
      this.missingEmployment = true;
      this.requestUpdate();
    }
    return Promise.resolve();
  }

  saveClicked() {
    // requestSubmit first will raise error with correct error message
    stratum.publishTag(TagKey.PQ_CONFIRMATION_SAVE_CLICKED, {
      replacements: {
        value: `${this.isPrimary ? '' : 'co borrower '}employment`,
      },
    });
    const form = this.shadowRoot.getElementById(this.cardFormId);
    form.requestSubmit();
  }

  @validateForm()
  async onSubmit() {
    // Check validity, update Application, then close editing card
    // activeIncome not selected + employed

    this.debug.log('Values:', this.values);
    this.debug.log('Validity before check', this.validity);

    this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
      this.values.job
    );
    this.checkMissingEmployment().then(() => {
      let valid = true;
      Object.values(this.validity).forEach((validityValue) => {
        if (!validityValue) {
          valid = false;
        }
      });
      this.debug.log('Values:', this.values);
      this.debug.log('Validity after check', this.validity);
      if (valid) {
        const cardComponent = this.shadowRoot.getElementById(
          `${this.coAppPrefix}employment-edit-card`
        );
        this.saveToApplication();
        this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
          this.values.job
        );
        stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_EMPLOYMENT_STATUS, {
          replacements: {
            value: `${this.isPrimary ? '' : 'co '}employment status: ${
              this.values.job
            }`,
          },
        });

        cardComponent?.closeEditing();
        this.displayError = false;
        this.tagFieldChangesOnSubmit();

        this.dispatchEvent(
          new CustomEvent('edit-card-closed', {
            bubbles: true,
            composed: true,
            detail: {
              component: `${this.coAppPrefix}refi-personal-employment-v2`,
            },
          })
        );
      }
    });
  }

  tagFieldChangesOnSubmit() {
    // runs in flow and confirmation components
    if (this.confirmationPage) {
      this.fieldsToCheck = [
        'employmentStatus',
        'jobDescription',
        'activeIncomeVerification',
        'incomeAmount',
        'otherIncome',
      ];
    } else {
      this.fieldsToCheck = ['employmentStatus', 'jobDescription'];
    }

    this.fieldsToCheck.forEach((field) => {
      if (this.isPrimary) {
        const initialValue = this.initialValues[field];
        const currentValue =
          this.application.primaryApplicant.employmentInfo[field] ||
          this.application.primaryApplicant[field];

        if (initialValue !== currentValue) {
          this.initialValues[field] = currentValue;
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: `${field}`,
            },
          });
        }
      } else {
        const coAppInitialValue = this.initialCoAppValues[field];
        const coAppCurrentValue =
          this.application.coApplicant[field] ||
          this.application.coApplicant.employmentInfo[field];

        if (coAppCurrentValue !== coAppInitialValue) {
          this.initialCoAppValues[field] = coAppCurrentValue;
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: `co-${field}`,
            },
          });
        }
      }
    });
  }

  saveToApplication() {
    this.inputChildren.job = this.shadowRoot.getElementById(this.childIds.job);

    this.application[this.applicantType].employmentStatus = this.values.job;
    this.application[this.applicantType].employmentInfo.employerName =
      this.values.job;
    this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
      this.values.job
    );
    this.application[this.applicantType].employmentInfo.jobDescription = this
      .employedOrSelfEmployed
      ? this.inputChildren.job.values.jobDescription
      : '';

    this.inputChildren.income = this.shadowRoot.getElementById(
      this.childIds.income
    );
    if (this.inputChildren.income) {
      this.inputChildren.income.saveToApplication();
    }

    this.inputChildren.employmentActiveToggle = this.shadowRoot.getElementById(
      this.childIds.employmentActiveToggle
    );

    if (
      this.employedOrSelfEmployed &&
      this.inputChildren.employmentActiveToggle
    ) {
      this.application[
        this.applicantType
      ].employmentInfo.activeIncomeVerification =
        this.inputChildren.employmentActiveToggle.values.activeIncomeVerification;
    } else {
      this.application[
        this.applicantType
      ].employmentInfo.activeIncomeVerification = '';
    }

    this.dispatchEmploymentChangeEvent();
    this.requestUpdate();

    this.dispatchEvent(
      new CustomEvent('host-app-confirmation-page-event', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'update-application-personal',
          data: { application: this.application },
        },
      })
    );
  }

  formatEmploymentStatus(employmentStatus) {
    const statusMap = {
      [REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED]:
        'Self-Employed/1099 contractor',
      [REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED]: 'Employed',
      [REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.UNEMPLOYED]: 'Unemployed',
      [REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.RETIRED]: 'Retired',
      [REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.OTHER]: 'Other',
    };
    return statusMap[employmentStatus?.toLowerCase()] || 'Other';
  }
}

customElements.define(
  'refi-personal-employment-v2',
  RefiPersonalEmploymentComponent
);

// Defining it again so that we can import a separate instance for coapp on confirmation page
customElements.define(
  'co-refi-personal-employment-v2',
  class extends RefiPersonalEmploymentComponent {}
);
